import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { IconBtn, MoreMenu } from "@/components/app/IconAction";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { MultiPick, dl } from "@/components/order/ServiceReports";
import { api, errMsg } from "@/lib/api";
import { fmtDate, today } from "@/lib/format";
import { useFetch } from "@/hooks/useFetch";
import { isService } from "@/lib/orderFields";

const REPORT_TYPES = ["Service Report", "Commissioning Report", "Installation Report"];
export const SERVICE_OPEN = ["approved", "in_production", "partially_dispatched"];
const sel = "h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm";

const held = (o, lid) => (o.service_completions || []).filter((c) => c.line_item_id === lid && c.status === "waiting_pod").reduce((s, c) => s + c.qty, 0);
const leftOf = (o, l) => Math.max(l.qty - (l.completed_qty || 0) - (l.short_closed_qty || 0) - held(o, l.id), 0);

function ProofFields({ ln, f, set, o, files, setFiles }) {
  if (ln.fulfilment_proof !== "POD") return (
    <>
      <div><Label>Report type *</Label><select className={sel} value={f.report_type} onChange={set("report_type")} data-testid="sc-report-type">{REPORT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
      <div><Label>Report no. (from the service app)</Label><Input value={f.report_no} onChange={set("report_no")} maxLength={60} data-testid="sc-report-no" /></div>
      <div className="col-span-2"><Label>Report file(s) * - PDF, scans or photos</Label><MultiPick files={files} setFiles={setFiles} testid="sc-files" /></div>
    </>
  );
  const live = (o.dispatches || []).filter((d) => d.dispatch_status !== "cancelled");
  return (
    <>
      <div className="col-span-2 flex flex-wrap gap-4 text-sm" data-testid="sc-pod-mode">
        <label className="flex items-center gap-1"><input type="radio" checked={f.pod_mode === "dispatch"} onChange={() => set("pod_mode")({ target: { value: "dispatch" } })} data-testid="sc-pod-mode-dispatch" />Covered by dispatch POD</label>
        <label className="flex items-center gap-1"><input type="radio" checked={f.pod_mode === "upload"} onChange={() => set("pod_mode")({ target: { value: "upload" } })} data-testid="sc-pod-mode-upload" />Upload POD (transport / freight etc.)</label>
      </div>
      {f.pod_mode === "dispatch" ? <div className="col-span-2"><Label>Dispatch *</Label>
        <select className={sel} value={f.dispatch_id} onChange={set("dispatch_id")} data-testid="sc-dispatch">
          <option value="">Pick a dispatch of this LM</option>{live.map((d) => <option key={d.id} value={d.id}>{d.dispatch_no} · POD {d.pod_status}</option>)}
        </select>{!live.length && <p className="mt-1 text-xs text-amber-700">No dispatch on this LM yet - upload the POD instead.</p>}</div>
        : <div className="col-span-2"><Label>POD file(s) *</Label><MultiPick files={files} setFiles={setFiles} testid="sc-files" /></div>}
    </>
  );
}

function UnitsPick({ orderId, ids, setIds }) {
  const { data } = useFetch(`/orders/${orderId}/service-asset-links`);
  useEffect(() => { if (data) { setIds(data.default_ids || []); } }, [data, setIds]);
  if (!data) return null;
  if (!data.group) return <p className="col-span-2 text-xs text-amber-800" data-testid="sc-no-group">No asset group linked - the report is saved on the LM only (link one from the Service Reports Register).</p>;
  const tog = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="col-span-2" data-testid="sc-units"><Label>Asset units covered (group {data.group.asset_group_code})</Label>
      <div className="mt-1 max-h-32 overflow-y-auto rounded border border-slate-200 p-2 grid grid-cols-2 gap-1 text-xs">
        {data.units.map((u) => <label key={u.id} className="flex items-center gap-1"><input type="checkbox" checked={ids.includes(u.id)} onChange={() => tog(u.id)} data-testid={`sc-unit-${u.asset_code}`} />{u.asset_code} {u.unit_name}</label>)}
        {!data.units.length && <span className="text-slate-400">No units in this group</span>}</div></div>
  );
}

function AskDialog({ title, text, children, okLabel, okDisabled, onOk, onBack, testid }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onBack()}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {text && <p className="text-sm text-slate-700">{text}</p>}{children}
        <DialogFooter><Button variant="outline" onClick={onBack} data-testid={`${testid}-back`}>Back</Button><Button disabled={okDisabled} onClick={onOk} data-testid={`${testid}-ok`}>{okLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Record service completion: line, qty, date, done by, proof (report upload / POD), asset units covered
export function ServiceCompletionDialog({ o, onClose, onDone, lineId }) {
  const lines = o.line_items.filter(isService).filter((l) => leftOf(o, l) > 0);
  const [f, setF] = useState({ line: lineId || lines[0]?.id || "", qty: "", date: today(), done_by: "", remarks: "", report_type: REPORT_TYPES[0], report_no: "", pod_mode: "dispatch", dispatch_id: "" });
  const [files, setFiles] = useState([]);
  const [units, setUnits] = useState([]);
  const [warn, setWarn] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ln = lines.find((l) => l.id === f.line);
  const pod = ln?.fulfilment_proof === "POD";
  const what = (f.report_type || "Service Report").replace(" Report", " report");
  const save = () => { if (o.material_undelivered && !pod) setWarn(true); else submit(); };
  const submit = async () => {
    setWarn(false);
    const fd = new FormData();
    Object.entries({ order_id: o.id, line_item_id: f.line, qty: f.qty, completion_date: f.date, done_by: f.done_by, remarks: f.remarks, report_type: f.report_type, report_no: f.report_no,
      pod_mode: pod ? f.pod_mode : "", dispatch_id: f.dispatch_id, asset_unit_ids: pod ? "" : units.join(",") }).forEach(([k, v]) => fd.append(k, v ?? ""));
    files.forEach((x) => fd.append("files", x));
    setBusy(true);
    try { await api.post("/service-completions", fd); toast.success("Service completion recorded"); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="service-completion-dialog">
        <DialogHeader><DialogTitle>Record service completion · {o.order_no}</DialogTitle></DialogHeader>
        {!lines.length ? <p className="text-sm text-slate-600" data-testid="sc-nothing-left">No service line has quantity left to complete.</p> : (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Service line *</Label><select className={sel} value={f.line} onChange={set("line")} data-testid="sc-line">
              {lines.map((l) => <option key={l.id} value={l.id}>{l.line_no}. {l.description} · {l.fulfilment_proof} · left {leftOf(o, l)} {l.uom}</option>)}</select></div>
            <div><Label>Completed qty * (max {ln ? leftOf(o, ln) : 0})</Label><Input type="number" min="0" value={f.qty} onChange={set("qty")} data-testid="sc-qty" /></div>
            <div><Label>Completion date *</Label><Input type="date" max={today()} value={f.date} onChange={set("date")} data-testid="sc-date" /></div>
            <div className="col-span-2"><Label>Done by * (own technician or vendor)</Label><Input value={f.done_by} onChange={set("done_by")} data-testid="sc-done-by" /></div>
            {ln && <ProofFields ln={ln} f={f} set={set} o={o} files={files} setFiles={setFiles} />}
            {ln && !pod && <UnitsPick orderId={o.id} ids={units} setIds={setUnits} />}
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={f.remarks} onChange={set("remarks")} data-testid="sc-remarks" /></div>
          </div>)}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button>{lines.length > 0 && <Button disabled={busy} onClick={save} data-testid="sc-save-btn">{busy ? "Saving…" : "Record completion"}</Button>}</DialogFooter>
        {warn && <AskDialog title="Material not delivered" text={`Material for this LM is not delivered yet - record ${what} anyway?`} okLabel="Continue" onOk={submit} onBack={() => setWarn(false)} testid="sc-warn" />}
      </DialogContent>
    </Dialog>
  );
}

function Completions({ rows, canEdit, reload }) {
  const [cur, setCur] = useState(null);
  const [reason, setReason] = useState("");
  const cancel = async () => {
    try { await api.post(`/service-completions/${cur.id}/cancel`, { reason }); toast.success("Completion cancelled"); setCur(null); setReason(""); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
    {cur && <AskDialog title={`Cancel completion of line ${cur.line_no} (${cur.qty})`} text="Its quantity is reversed and its file(s) are removed from the LM and the asset group / units." okLabel="Cancel completion" okDisabled={reason.trim().length < 3}
      onOk={cancel} onBack={() => setCur(null)} testid="sc-cancel-dialog"><Label>Reason * (at least 3 characters)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sc-cancel-reason" /></AskDialog>}
    <table className="w-full text-xs" data-testid="service-completions-table">
      <thead><tr className="th text-left">{["Line", "Qty", "Date", "Done by", "Proof", "Status", "By", ""].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
      <tbody>{rows.map((c) => (
        <tr key={c.id} className="border-b border-slate-100" data-testid={`service-completion-${c.id}`}>
          <td className="px-2 py-1">{c.line_no}. {c.description}</td><td className="px-2 py-1 font-mono">{c.qty}</td><td className="px-2 py-1">{fmtDate(c.completion_date)}</td><td className="px-2 py-1">{c.done_by}</td>
          <td className="px-2 py-1">{c.proof_type === "POD" ? (c.dispatch_no ? `POD of ${c.dispatch_no}` : "POD uploaded") : `${c.report_type}${c.report_no ? ` ${c.report_no}` : ""}`}</td>
          <td className="px-2 py-1"><StatusBadge status={c.status} text={c.status === "waiting_pod" ? "waiting for POD" : undefined} />{c.cancel_reason && <div className="text-[10px] text-slate-500">{c.cancel_reason}</div>}</td>
          <td className="px-2 py-1 text-slate-500">{c.created_by?.name}</td>
          <td className="w-10 px-2 py-1"><MoreMenu testid={`sc-more-${c.id}`} groups={[["Download", (c.doc_ids || []).map((id, i) => [id, `Proof file ${i + 1}`, () => dl({ id, stored_name: `${c.order_no}_proof_${i + 1}` }), `sc-file-${id}`])],
            ["Completion", canEdit && c.status !== "cancelled" ? [["cancel", "Cancel completion…", () => setCur(c), `sc-cancel-${c.id}`]] : []]]} /></td>
        </tr>))}
        {!rows.length && <tr><td colSpan={8} className="px-2 py-4 text-center text-slate-400">No service completions yet</td></tr>}
      </tbody>
    </table>
    </>
  );
}

export function ServiceCompletionDialogFor({ orderId, lineId, onClose, onDone }) {
  const { data } = useFetch(`/orders/${orderId}`);
  return data ? <ServiceCompletionDialog o={data} lineId={lineId} onClose={onClose} onDone={onDone} /> : null;
}

// LM detail "Service" tab: service lines with completed qty and their completions
export function ServiceTab({ o, reload }) {
  const { can } = useMe();
  const [dlg, setDlg] = useState(false);
  const lines = o.line_items.filter(isService);
  const ok = can("service.create") && SERVICE_OPEN.includes(o.status) && !o.is_projection;
  return (
    <div data-testid="service-tab">
      {!lines.length && <p className="mb-3 text-sm text-slate-500">This LM has no service lines.</p>}
      {lines.length > 0 && <table className="mb-4 w-full text-sm" data-testid="service-lines-table">
        <thead><tr className="th text-left">{["#", "Service line", "Proof", "Qty", "Completed", "Waiting POD", "Left", ""].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
        <tbody>{lines.map((l) => <tr key={l.id} className="border-b border-slate-100" data-testid={`service-line-${l.line_no}`}>
          <td className="px-2 py-1 font-mono text-xs">{l.line_no}</td><td className="px-2 py-1">{l.description}</td><td className="px-2 py-1 text-xs">{l.fulfilment_proof}</td>
          <td className="px-2 py-1 font-mono">{l.qty} {l.uom}</td><td className="px-2 py-1 font-mono" data-testid={`service-line-completed-${l.line_no}`}>{l.completed_qty || 0}</td>
          <td className="px-2 py-1 font-mono">{held(o, l.id) || "—"}</td><td className="px-2 py-1 font-mono">{leftOf(o, l)}</td>
          <td className="w-10 px-2 py-1">{ok && leftOf(o, l) > 0 && <IconBtn icon={Upload} label={l.fulfilment_proof === "POD" ? "Upload POD" : "Upload report"} className="text-sky-700 hover:bg-sky-50" onClick={() => setDlg(l.id)} testid={`service-line-upload-${l.line_no}`} />}</td></tr>)}</tbody>
      </table>}
      {ok && lines.length > 0 && <Button size="sm" className="mb-3" onClick={() => setDlg(true)} data-testid="service-tab-record-btn">Record service completion</Button>}
      <Completions rows={o.service_completions || []} canEdit={can("service.edit")} reload={reload} />
      {dlg && <ServiceCompletionDialog o={o} lineId={typeof dlg === "string" ? dlg : undefined} onClose={() => setDlg(false)} onDone={() => { setDlg(false); reload(); }} />}
    </div>
  );
}
